// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

import { Loader2 } from 'lucide-react';
import { useLoginCodeStep } from '../hooks/useLoginCodeStep';
import type { LoginChallenge } from '../types/login-outcome';

interface LoginCodeFormProps {
  challenge: LoginChallenge;
  /** Returns to the password step, discarding this challenge. */
  onBack: () => void;
}

/**
 * Second step of the login: the password was accepted, now the code that was
 * emailed to the user is needed to finish. Rendered inside the login card.
 */
export function LoginCodeForm({ challenge, onBack }: LoginCodeFormProps) {
  const step = useLoginCodeStep(challenge);
  const expiresInMinutes = Math.max(1, Math.round(challenge.expiresInSeconds / 60));

  return (
    <>
      <h1 className="text-[18px] font-semibold text-card-foreground">Check your email</h1>
      <p className="mt-1 text-[13px] text-muted-foreground">
        We sent a {step.codeLength}-digit code to{' '}
        <span className="font-medium text-foreground">{challenge.email}</span>. Enter it below to
        finish signing in. The code expires in {expiresInMinutes} minutes.
      </p>

      {step.errorMessage && (
        <div
          className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[13px] text-destructive"
          role="alert"
        >
          {step.errorMessage}
        </div>
      )}

      {step.resendMessage && !step.errorMessage && (
        <div
          className="mt-4 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-[13px] text-foreground"
          role="status"
        >
          {step.resendMessage}
        </div>
      )}

      {step.challengeDead ? (
        <button
          type="button"
          onClick={onBack}
          className="mt-6 flex w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          Back to sign in
        </button>
      ) : (
        <form className="mt-6 space-y-4" onSubmit={step.submit} noValidate>
          <div>
            <label
              htmlFor="login-code"
              className="mb-1.5 block text-[13px] font-medium text-foreground"
            >
              Confirmation code
            </label>
            <input
              id="login-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              autoFocus
              maxLength={step.codeLength}
              value={step.code}
              onChange={(e) => step.setCode(e.target.value)}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-center font-mono text-[22px] tracking-[0.4em] text-foreground placeholder:tracking-[0.4em] placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="······"
              required
              aria-invalid={!!step.fieldError}
              aria-describedby={step.fieldError ? 'login-code-error' : undefined}
            />
            {step.fieldError && (
              <p id="login-code-error" className="mt-1 text-[12px] text-destructive">
                {step.fieldError}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={step.isVerifying || step.code.length !== step.codeLength}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
          >
            {step.isVerifying && <Loader2 className="h-4 w-4 animate-spin" />}
            Verify code
          </button>
        </form>
      )}

      <div className="mt-4 flex items-center justify-between text-[13px] text-muted-foreground">
        <button type="button" onClick={onBack} className="font-medium text-primary hover:underline">
          Use a different account
        </button>
        {!step.challengeDead && (
          <button
            type="button"
            onClick={step.resendCode}
            disabled={step.resendCooldown > 0 || step.isResending}
            className="font-medium text-primary hover:underline disabled:pointer-events-none disabled:text-muted-foreground"
          >
            {step.isResending
              ? 'Sending…'
              : step.resendCooldown > 0
                ? `Resend code in ${step.resendCooldown}s`
                : 'Resend code'}
          </button>
        )}
      </div>
    </>
  );
}
